import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@shared/lib/utils';

const EMPTY_VALUE = '__empty__';

interface SelectFieldProps {
  icon?: ReactNode;
  label?: string;
  options: { value: string; label: string }[];
  value?: string;
  disabled?: boolean;
  className?: string;
  onChange?: (event: { target: { value: string } }) => void;
}

export function SelectField({
  icon,
  label,
  options,
  value,
  disabled = false,
  className = '',
  onChange,
}: SelectFieldProps) {
  const radixValue = value === '' ? EMPTY_VALUE : value;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      {icon && <span className="shrink-0 text-text-secondary">{icon}</span>}
      <div className="min-w-0 flex-1">
        {label && <div className="mb-1 text-xs text-text-secondary">{label}</div>}
        <Select.Root
          value={radixValue}
          disabled={disabled}
          onValueChange={(nextValue) =>
            onChange?.({ target: { value: nextValue === EMPTY_VALUE ? '' : nextValue } })
          }
        >
          <Select.Trigger
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-lg border border-surface-border bg-surface-2 px-3 py-2 text-left text-sm text-text-primary outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50',
              className,
            )}
          >
            <Select.Value />
            <Select.Icon asChild>
              <ChevronDown className="h-4 w-4 shrink-0 text-text-muted" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              position="popper"
              sideOffset={6}
              className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-surface-border bg-surface-2 p-1 text-sm text-text-primary shadow-lg"
            >
              <Select.Viewport>
                {options.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value === '' ? EMPTY_VALUE : option.value}
                    className="relative flex cursor-default select-none items-center rounded-md py-2 pl-8 pr-3 outline-none data-[highlighted]:bg-surface-3 data-[highlighted]:text-text-primary"
                  >
                    <Select.ItemIndicator className="absolute left-2 flex h-4 w-4 items-center justify-center text-accent">
                      <Check className="h-4 w-4" />
                    </Select.ItemIndicator>
                    <Select.ItemText>{option.label}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
    </div>
  );
}
